import { AppConfig } from '@/types/AppConfig.type';
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { logout } from './authSlice';
import { applyInspectConfigOverrides } from '@/config/applyInspectConfigOverrides';
import { systemConfig } from '@/config/systemConfig';

export interface AppConfigState extends AppConfig {
  configFetchState: 'idle' | 'loading' | 'success' | 'failed';
}

const initialState: AppConfigState = {
  sk: '',
  configFetchState: 'idle',
  sentry: undefined,
  inspections: undefined,
  login: {
    clientId: '',
    url: '',
    issuer: '',
  },
  qcInspection: {
    autoCloseInspection: false,
  },
  ['record-defect']: {
    isDefectImageRequired: false,
  },
  ['inspector-comment-length']: 0,
  ['size-check']: {
    passes: 3,
    decimalPlaces: 2,
    passesTitles: ['Pass 1', 'Pass 2', 'Pass 3'],
  },
  ['start-inspection-check']: false,
  ['show-factory']: false,
  ['mandatory-module-check']: false,
};
export const loadConfig = createAsyncThunk('appConfig/load', async (): Promise<AppConfigState> => {
  const inspectConfigResponse = await fetch(systemConfig.inspectConfigUrl);
  const configResponse = await fetch(systemConfig.configUrl);
  const inspectConfig = await inspectConfigResponse.json();
  const config = await configResponse.json();
  const fullConfig = applyInspectConfigOverrides({ ...inspectConfig, ...config });
  return { ...fullConfig, configFetchState: 'success' };
});

const appConfigSlice = createSlice({
  name: 'appConfig',
  initialState,
  reducers: {
    setConfig: (state, action: PayloadAction<AppConfigState>) => {
      const config = action.payload;
      Object.assign(state, config, { configFetchState: 'success' });
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadConfig.pending, (state) => {
        state.configFetchState = 'loading';
      })
      .addCase(loadConfig.fulfilled, (state, action) => {
        const config = action.payload;
        Object.assign(state, config, { configFetchState: 'success' });
      })
      .addCase(logout, () => initialState); // Reset state on logout
  },
});

export const { setConfig } = appConfigSlice.actions;
export default appConfigSlice.reducer;
